import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faArrowDown,
    faFilter,
    faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { cairoDate } from "../../utils/cairoDate";
import { calculateTaskStatus } from "../../utils/calculateTaskStatus";
import { roundNumber } from "../../utils/roundNumber";

const taskImportance = ["عادية", "متوسطة", "قصوى"];

const TmsMyTasksCollapse = ({
    title,
    tasks = [],
    bgColor = "bg-[#5268b1]",
    hoverColor = "hover:bg-[#46599b]",
    system,
}) => {
    const navigate = useNavigate();
    const clickTimeout = useRef(null);
    const [collapseOpen, setCollapseOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedImportance, setSelectedImportance] = useState([]);
    const [arrangeTasks, setArrangeTasks] = useState(true);

    const handleArrange = () => {
        setArrangeTasks(prev => !prev);
    }

    const handleClick = (task) => {
        // If there's already a timeout, it means a double-click occurred
        if (clickTimeout.current) {
            clearTimeout(clickTimeout.current);
            clickTimeout.current = null;
            navigate(`/${system}/tms/edit/${task.id}`)
        } else {
            // Otherwise, set a timer for single click
            clickTimeout.current = setTimeout(() => {
                clickTimeout.current = null;
            }, 250); // delay to detect double-click (250ms is a good default)
        }
    };

    const toggleSelectAll = () => {
        if (selectedImportance.length === taskImportance.length) {
            setSelectedImportance([]); // deselect all
        } else {
            setSelectedImportance([...taskImportance]); // select all
        }
    };

    const toggleImportance = (size) => {
        if (selectedImportance.includes(size)) {
            setSelectedImportance(selectedImportance.filter((item) => item !== size));
        } else {
            setSelectedImportance([...selectedImportance, size]);
        }
    };

    const statusPercentage = (status) => {
        if (status === "not started yet" || status === "in progress") {
            return "0%";
        } else if (status === "finished" || status === "submitted") {
            return "100%";
        } else if (status === "0" || status === "25" || status === "50" || status === "75") {
            return `${status}%`
        } else {
            return status;
        }
    }

    const calculateTaskOverallScore = (data) => {
        const finishedTasksPercentage = statusScore(data?.assignee_status);
        const avgManagerSpeed = Number(data?.manager_speed);
        const avgManagerQuality = Number(data?.manager_quality);
        const avgManagerStatus = Number(data?.manager_status);
        const avgReviewerSpeed = Number(data?.reviewer_speed);
        const avgReviewerQuality = Number(data?.reviewer_quality);
        const avgReviewerStatus = Number(data?.reviewer_status);
        return roundNumber(((finishedTasksPercentage * 0.4) + (((avgManagerSpeed + avgReviewerSpeed + avgReviewerStatus) / 3) * 0.3) + (((avgManagerQuality + avgReviewerQuality + avgManagerStatus) / 3) * 0.3)))
    }

    const statusScore = (status) => {
        if (status === "not started yet" || status === "in progress") {
            return 0;
        } else if (status === "finished" || status === "submitted") {
            return 100;
        } else if (status === "0" || status === "25" || status === "50" || status === "75") {
            return status
        } else {
            return 0;
        }
    }

    const displayedTasks = arrangeTasks
        ? tasks
        : [...tasks].reverse();

    return (
        <div className="w-[95%] bg-white rounded-2xl shadow-md overflow-hidden">
            <div
                className={`flex justify-between items-center px-5 py-4 cursor-pointer text-white transition-colors ${bgColor} ${hoverColor}`}
                onClick={() => setCollapseOpen(!collapseOpen)}
            >
                <div className="flex items-center gap-2">
                    <span className="bg-white/20 px-3 py-1 rounded-full text-xs">
                        {tasks.length}
                    </span>
                    <span className="text-sm">مهمة</span>
                </div>

                <div className="flex items-center gap-3">
                    <h1 className="font-bold text-lg">{title}</h1>

                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`transition-transform duration-300 ${collapseOpen ? "rotate-180" : ""
                            }`}
                    />
                </div>
            </div>

            <div
                className={`overflow-hidden transition-all duration-300 ${collapseOpen ? "max-h-[5000px]" : "max-h-0"
                    }`}
            >
                <div className="p-4 bg-gray-50">
                    <div className="flex flex-col w-full gap-2">
                        <div className="flex gap-2 w-full" dir="rtl">
                            <div className={`text-white text-center rounded p-2 ${bgColor} flex-1 text-sm max-w-20`}>
                                م
                            </div>

                            <div
                                className={`relative z-40 flex-1 ${bgColor} p-2 rounded flex justify-center items-center`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(!isOpen);
                                }}
                            >
                                <div className="relative text-white text-center text-xs w-full flex justify-center items-center">
                                    <h1>الاولوية</h1>
                                </div>

                                {isOpen && (
                                    <div className="absolute top-full mt-1 left-0 w-full bg-[#2f417a] border border-gray-400 rounded shadow-lg z-50">
                                        <div
                                            className="p-2 flex items-center gap-2 text-white text-sm hover:bg-[#5268b1] cursor-pointer transition-colors"
                                            onClick={toggleSelectAll}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedImportance.length === taskImportance.length}
                                                readOnly
                                                className="accent-[#3fd8ff] cursor-pointer"
                                            />
                                            <span>تحديد الكل</span>
                                        </div>

                                        <div className="border-t border-gray-500" />

                                        {taskImportance.map((size, i) => (
                                            <div
                                                key={i}
                                                onClick={() => toggleImportance(size)}
                                                className="px-2 py-1 flex items-center gap-2 text-white text-sm hover:bg-[#5268b1] cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedImportance.includes(size)}
                                                    readOnly
                                                    className="accent-[#3fd8ff] cursor-pointer"
                                                />
                                                <span>{size}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className={`relative text-white text-center rounded p-2 ${bgColor} flex-1 text-xs flex justify-center items-center`}>
                                <h1>حجم المهمة</h1>
                            </div>

                            <div className={`relative text-white text-center rounded p-2 ${bgColor} flex-1 text-xs flex justify-center items-center`}>
                                <h1>تاريخ و توقيت البدء</h1>
                            </div>

                            <div className={`relative text-white text-center rounded p-2 ${bgColor} flex-1 text-xs flex justify-evenly items-center`}>
                                <FontAwesomeIcon
                                    icon={arrangeTasks ? faArrowDown : faArrowUp}
                                    onClick={handleArrange}
                                    className="text-md text-white cursor-pointer"
                                />
                                <div className="w-0 h-3 border-white border-l-2" />
                                <h1>تاريخ و توقيت التسليم</h1>
                            </div>

                            <div className={`relative text-white text-center rounded p-2 ${bgColor} flex-1 text-xs flex justify-center items-center`}>
                                <h1>نسبة اكتمال المهمة</h1>
                            </div>

                            <div className={`relative text-white text-center rounded p-2 ${bgColor} flex-1 text-xs flex justify-center items-center`}>
                                <h1>تقييم الموقف التنفيذي</h1>
                            </div>

                            <div className={`relative text-white text-center rounded p-2 ${bgColor} flex-1 text-xs flex justify-center items-center`}>
                                <h1>اجمالي التقييم</h1>
                            </div>
                        </div>

                        {displayedTasks.map((task, idx) => (
                            <div
                                key={task.id}
                                className="flex gap-2 w-full cursor-pointer hover:scale-[1.002] transition-transform"
                                dir="rtl"
                                onClick={() => handleClick(task)}
                            >
                                <div className={`relative text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : new Date(task.end_date) < new Date() ? "bg-red-500" : "bg-white"} flex-1 text-sm flex justify-center items-center max-w-20 shadow-sm`}>
                                    {idx + 1}
                                    <div className="absolute right-2">
                                        <FontAwesomeIcon icon={faArrowDown} className="text-lg -rotate-45" />
                                    </div>
                                </div>

                                <div className={`text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm`}>
                                    {task.importance}
                                </div>

                                <div className={`text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm`}>
                                    {task.size}
                                </div>

                                <div className={`text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm`}>
                                    {cairoDate(task.start_date)}
                                </div>

                                <div className={`text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm`}>
                                    {cairoDate(task.end_date)}
                                </div>

                                <div className={`text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm`}>
                                    {statusPercentage(task.assignee_status)}
                                </div>

                                <div className={`text-black text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm`}>
                                    {calculateTaskStatus(task)}
                                </div>

                                <div
                                    className={`text-center rounded p-2 ${task.assignee_status === 100 ? "bg-green-500" : "bg-white"} flex-1 text-sm flex justify-center items-center shadow-sm ${calculateTaskOverallScore(task) < 50
                                        ? "text-red-500 font-bold"
                                        : "text-black"
                                        }`}
                                >
                                    {calculateTaskOverallScore(task)}%
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TmsMyTasksCollapse;